import { Flex, Icon, Text } from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'

import { useReportingI18n } from '../i18n'
import { getWallClockNowInTimeZone } from '../utils/LocalTimeHelper'
import { DateRange } from './DateRange'
import { DateTimeRangeModal } from './DateTimeRangeModal'
import { DropdownMangoButton } from './dropdown/DropdownMangoButton'
import { MenuPlacement } from './dropdown/Menu'
import { MenuListItem } from './dropdown/MenuItem'
import { CalendarIcon, CheckIcon } from './icons'
import { MangoButtonVariant } from './MangoButton'
import { hasValue } from './utils'

enum DateDropdownValue {
  Today = 'dateDropdown.today',
  Yesterday = 'dateDropdown.yesterday',
  Last7Days = 'dateDropdown.last7Days',
  Last14Days = 'dateDropdown.last14Days',
  Last30Days = 'dateDropdown.last30Days',
  Last60Days = 'dateDropdown.last60Days',
  Last90Days = 'dateDropdown.last90Days',
  CustomRange = 'dateDropdown.customRange',
}

const DATE_DROPDOWN_LABELS: Record<string, string> = {
  [DateDropdownValue.Today]: 'Today',
  [DateDropdownValue.Yesterday]: 'Yesterday',
  [DateDropdownValue.Last7Days]: 'Last 7 Days',
  [DateDropdownValue.Last14Days]: 'Last 14 Days',
  [DateDropdownValue.Last30Days]: 'Last 30 Days',
  [DateDropdownValue.Last60Days]: 'Last 60 Days',
  [DateDropdownValue.Last90Days]: 'Last 90 Days',
  [DateDropdownValue.CustomRange]: 'Custom Range',
}

type DateRangeFactory = () => DateRange

interface DateItemData {
  label: DateDropdownValue
  getValue?: DateRangeFactory
}

interface DateRangeDropdownProps {
  dateTimeRange?: DateRange
  onValueChange?: (dateRange: DateRange) => void
  useExtendedDates?: boolean
  isHeatmap?: boolean
  menuPlacement?: MenuPlacement
  /** IANA timezone whose calendar the presets follow; the viewer's if unset. */
  timeZone?: string
  /** Custom Range picks whole days from a calendar, with no times. */
  dateOnly?: boolean
}

export const DateTimeRangeDropdown = ({
  dateTimeRange: externalDateRange,
  onValueChange,
  useExtendedDates = false,
  isHeatmap = false,
  menuPlacement = MenuPlacement.BottomRight,
  timeZone,
  dateOnly = false,
}: DateRangeDropdownProps) => {
  const { text } = useReportingI18n()

  const preset =
    (factory: (now?: Date) => DateRange): DateRangeFactory =>
    () =>
      factory(timeZone ? getWallClockNowInTimeZone(timeZone) : new Date())
  const today = preset(DateRange.today)
  const yesterday = preset(DateRange.yesterday)
  const last7Days = preset(DateRange.last7Days)
  const last14Days = preset(DateRange.last14Days)
  const last30Days = preset(DateRange.last30Days)
  const last60Days = preset(DateRange.last60Days)
  const last90Days = preset(DateRange.last90Days)

  const translate = (key: string): string => {
    return text(key, DATE_DROPDOWN_LABELS[key] ?? key)
  }

  const dateItems: DateItemData[] = [
    ...(isHeatmap
      ? []
      : [
          {
            label: DateDropdownValue.Today,
            getValue: today,
          },
          {
            label: DateDropdownValue.Yesterday,
            getValue: yesterday,
          },
        ]),
    {
      label: DateDropdownValue.Last7Days,
      getValue: last7Days,
    },
    {
      label: DateDropdownValue.Last14Days,
      getValue: last14Days,
    },
    {
      label: DateDropdownValue.Last30Days,
      getValue: last30Days,
    },
    ...(useExtendedDates
      ? [
          {
            label: DateDropdownValue.Last60Days,
            getValue: last60Days,
          },
          {
            label: DateDropdownValue.Last90Days,
            getValue: last90Days,
          },
        ]
      : []),
    {
      label: DateDropdownValue.CustomRange,
    },
  ]

  const dateRangeIndices = dateItems.reduce<Record<string, number>>(
    (accum, item, index) => {
      accum[item.label] = index
      return accum
    },
    {},
  )

  const debugDateRangeComparison = (
    _name: string,
    dateRange: DateRange,
    func: () => DateRange,
  ) => {
    const generatedRange = func()
    const isEqual = DateRange.areEqual(generatedRange, dateRange)
    return isEqual
  }

  const getIndexFromDateRange = (dateRange: DateRange) => {
    const isLast30Days = debugDateRangeComparison(
      'Last30Days',
      dateRange,
      last30Days,
    )
    if (isLast30Days) {
      return dateRangeIndices[DateDropdownValue.Last30Days]
    }

    const isToday = debugDateRangeComparison('Today', dateRange, today)
    if (isToday) {
      return dateRangeIndices[DateDropdownValue.Today]
    }

    const isYesterday = debugDateRangeComparison(
      'Yesterday',
      dateRange,
      yesterday,
    )
    if (isYesterday) {
      return dateRangeIndices[DateDropdownValue.Yesterday]
    }

    const isLast7Days = debugDateRangeComparison(
      'Last7Days',
      dateRange,
      last7Days,
    )
    if (isLast7Days) {
      return dateRangeIndices[DateDropdownValue.Last7Days]
    }

    const isLast14Days = debugDateRangeComparison(
      'Last14Days',
      dateRange,
      last14Days,
    )
    if (isLast14Days) {
      return dateRangeIndices[DateDropdownValue.Last14Days]
    }

    const isLast60Days = debugDateRangeComparison(
      'Last60Days',
      dateRange,
      last60Days,
    )
    if (isLast60Days) {
      return dateRangeIndices[DateDropdownValue.Last60Days]
    }

    const isLast90Days = debugDateRangeComparison(
      'Last90Days',
      dateRange,
      last90Days,
    )
    if (isLast90Days) {
      return dateRangeIndices[DateDropdownValue.Last90Days]
    }

    return dateRangeIndices[DateDropdownValue.CustomRange]
  }

  const [selectedDateItemIndex, setSelectedDateItemIndex] = useState(0)
  const [dateRange, setDateRange] = useState<DateRange>(
    externalDateRange ?? DateRange.empty,
  )

  const [dateRangeModalOpen, setDateRangeModalOpen] = useState(false)

  const dateItemLabels = useMemo(() => {
    return dateItems.map((item, index) => {
      const content =
        index === selectedDateItemIndex ? (
          <MenuListItem
            primaryText={translate(item?.label)}
            rightContent={
              <Icon boxSize="1rem">
                <CheckIcon />
              </Icon>
            }
          />
        ) : (
          translate(item?.label)
        )
      return {
        value: translate(item?.label?.toString()),
        content,
        buttonLabel: translate(item?.label),
      }
    })
  }, [selectedDateItemIndex, dateItems])

  const handleDateItemSelected = (
    value: string,
    index: number,
    openModal = true,
  ) => {
    const isCustomRange =
      dateItems?.[index]?.label === DateDropdownValue.CustomRange

    if (isCustomRange) {
      if (openModal) {
        setDateRangeModalOpen(true)
        return
      }

      setSelectedDateItemIndex(index)

      if (externalDateRange) {
        setDateRange(externalDateRange)
        onValueChange?.(externalDateRange)
      }

      return
    }

    setSelectedDateItemIndex(index)
    if (!dateItems?.[index]) {
      return
    }

    const { getValue } = dateItems[index]
    if (!getValue) {
      return
    }

    const range = getValue()
    setDateRange(range)
    onValueChange?.(range)
  }

  useEffect(() => {
    if (!externalDateRange) return

    const dateRangeHasValue = hasValue(dateRange)
    const areDateRangesEqual = DateRange.areEqual(dateRange, externalDateRange)
    const targetIndex = getIndexFromDateRange(externalDateRange)
    const areIndicesEqual = selectedDateItemIndex === targetIndex
    const shouldHandleSelection =
      dateRangeHasValue && (!areDateRangesEqual || !areIndicesEqual)

    if (shouldHandleSelection) {
      handleDateItemSelected(
        DateDropdownValue.CustomRange.toString(),
        targetIndex ?? 0,
        false,
      )
      return
    }
  }, [externalDateRange])

  useEffect(() => {
    const needsInitialSet =
      (!hasValue(externalDateRange) ||
        DateRange.areEqual(externalDateRange, DateRange.empty)) &&
      DateRange.areEqual(dateRange, DateRange.empty)

    if (!needsInitialSet) {
      return
    }

    handleDateItemSelected(
      DateDropdownValue.Today.toString(),
      dateRangeIndices[DateDropdownValue.Today],
    )
  }, [])

  const handleDateRangeModalClose = () => {
    setDateRangeModalOpen(false)
  }

  const handleDateRangeModalSet = (dateTimeRange: DateRange) => {
    handleDateRangeModalClose()
    setDateRange(dateTimeRange)

    setSelectedDateItemIndex(dateRangeIndices[DateDropdownValue.CustomRange])
    onValueChange?.(dateTimeRange)
  }

  const handleDateRangeModalClear = () => {
    setDateRange({ ...DateRange.empty })
  }

  const formatBound = (date?: Date) =>
    dateOnly ? date?.toLocaleDateString() : date?.toLocaleString()

  const tooltipLabel: string | React.ReactNode = useMemo(() => {
    const selectedItem = dateItemLabels[selectedDateItemIndex]

    const beforeText = hasValue(dateRange.after)
      ? text('dateTimeRange.after', `After: ${formatBound(dateRange.after)}`)
      : undefined

    const afterText = hasValue(dateRange.before)
      ? text('dateTimeRange.before', `Before: ${formatBound(dateRange.before)}`)
      : undefined

    if (!selectedItem) {
      return (
        <Text color="fg.inverted" textAlign="center">
          {text('dateTimeRange.selectDateRange', 'Select Date Range')}
        </Text>
      )
    }

    return (
      <>
        {beforeText && (
          <Text color="fg.inverted" textAlign="center">
            {beforeText}
          </Text>
        )}
        {afterText && (
          <Text color="fg.inverted" textAlign="center">
            {afterText}
          </Text>
        )}
      </>
    )
  }, [
    dateItemLabels,
    selectedDateItemIndex,
    dateRange.before,
    dateRange.after,
    dateOnly,
  ])

  return (
    <Flex position="relative">
      <DropdownMangoButton
        variant={MangoButtonVariant.Default_200}
        items={dateItemLabels}
        selectedIndex={selectedDateItemIndex}
        onSelect={handleDateItemSelected}
        placeholder={text('dateTimeRange.buttonPlaceholder', 'Date Range')}
        tooltipLabel={tooltipLabel}
        leftIcon={CalendarIcon}
        menuOptions={{
          width: '18.75rem',
          maxHeight: '35vh',
          placement: menuPlacement,
        }}
      />
      <DateTimeRangeModal
        isOpen={dateRangeModalOpen}
        dateTimeRange={dateRange}
        dateOnly={dateOnly}
        onClear={handleDateRangeModalClear}
        onClose={handleDateRangeModalClose}
        onSet={handleDateRangeModalSet}
      />
    </Flex>
  )
}
